import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMeeting } from 'src/api/queries/meetings/useMeeting';
import {
  useDeleteMeeting,
  usePublishMeeting,
} from 'src/api/queries/meetings/useMeetingMutations';
import { useJobs } from 'src/hooks/useJobs';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Header } from 'src/design/organisms/Header';
import { Tabs } from 'src/design/molecules/Tabs';
import { NotificationHistory } from 'src/app/workflows/NotificationHistory';
import { MeetingForm } from 'src/routes/Jobs/MeetingMinutes/MeetingForm';

export const ViewMeetingMinutes: React.FC = () => {
  const { selectedJob } = useJobs();
  const params: any = useParams();
  const navigate = useNavigate();
  const { data: meeting, isLoading: loading } = useMeeting(
    selectedJob?.id,
    params.meetingId,
  );
  const [titleOverride, setTitleOverride] = React.useState<string | null>(null);
  const meetingType = titleOverride ?? meeting?.title ?? '';

  const deleteMeeting = useDeleteMeeting(selectedJob?.id ?? -1);
  const publishMeeting = usePublishMeeting(selectedJob?.id ?? -1);

  const deleteMeetingConfirmed = () => {
    if (!selectedJob || !meeting) return;
    deleteMeeting.mutate(meeting.id, {
      onSuccess: () => navigate(`/jobs/${selectedJob.id}/meetings`),
    });
  };

  const publishMeetingMinutes = () => {
    if (!meeting) return;
    publishMeeting.mutate(meeting.id);
  };

  if (!selectedJob) return null;

  if (loading || !meeting) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader />
      </Stack>
    );
  }

  return (
    <>
      <Header
        title={meetingType.length > 0 ? meetingType : 'Untitled Meeting'}
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            {
              text: 'Meeting Minutes',
              href: `/jobs/${selectedJob.id}/meetings`,
            },
            {
              text: meetingType.length > 0 ? meetingType : 'Untitled Meeting',
            },
          ],
        }}
        helpText="View and manage meeting details"
        helpTitle="Meeting Details"
      />

      <Group justify="flex-end" mb="md">
        <SecondaryButton
          onClick={() => navigate(`/jobs/${selectedJob.id}/meetings`)}
        >
          Back
        </SecondaryButton>
        <BasicButton
          onClick={publishMeetingMinutes}
          loading={publishMeeting.isPending}
        >
          Publish Meeting Notes
        </BasicButton>
        <BasicButton
          onClick={() =>
            navigate(
              `/jobs/${selectedJob.id}/meetings/export/${params.meetingId}`,
            )
          }
        >
          Export
        </BasicButton>
        <InlineConfirmButton
          onConfirmClick={deleteMeetingConfirmed}
          color="red"
          confirmButtonProps={{
            children: 'Delete Meeting',
            color: 'red',
          }}
          cancelButtonProps={{
            children: 'Cancel',
            color: 'black',
            variant: 'light',
          }}
        >
          Delete Meeting
        </InlineConfirmButton>
      </Group>

      <Tabs defaultValue="details" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" pt="md" keepMounted>
          <MeetingForm
            initialValues={meeting}
            jobId={selectedJob.id}
            onMeetingTypeUpdated={(val) => setTitleOverride(val)}
          />
        </Tabs.Panel>
        <Tabs.Panel value="notifications" pt="md">
          <Stack>
            <Text size="sm" c="dimmed">
              Invitees receive an email with the published PDF each time the
              meeting minutes are published.
            </Text>
            <NotificationHistory
              resourceType="meeting"
              entityNoun="meeting"
              jobId={selectedJob.id}
              entityId={meeting.id}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </>
  );
};
